import {
  BehaviorSubject,
  Observable,
  combineLatest,
  distinctUntilChanged,
  map,
  take,
  tap,
} from 'rxjs';
import { Component, DestroyRef, inject, Input, OnInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';

import {
  attachFeedAlgorithms,
  detachFeedAlgorithm,
  loadFeedAlgorithms,
} from 'src/app/core/actions/feed.actions';
import {
  selectAttachedFeedAlgorithms,
  selectAvailableFeedAlgorithms,
  selectFeedAlgorithmsLoading,
  selectPendingAlgorithmIds,
} from 'src/app/core/reducers';

import { AppState } from 'src/app/reducers';
import { FeedAlgorithm } from 'src/app/core/models/algorithm.models';
import { PostAlgorithmService } from 'src/app/core/services/post-algorithm.service';
import { ToolbarButton } from '../../../models/toolbar.models';

@Component({
  selector: 'app-feed-algorithms',
  templateUrl: './feed-algorithms.component.html',
  styleUrls: ['./feed-algorithms.component.scss'],
  standalone: false,
})
export class FeedAlgorithmsComponent implements OnInit {
  @Input() objectId!: string;
  @Input() objectClassId!: number;

  attachedAlgorithms$: Observable<FeedAlgorithm[]>;
  availableAlgorithms$: Observable<FeedAlgorithm[]>;
  loading$: Observable<boolean>;
  pendingAlgorithmIds$: Observable<number[]>;

  pickerMode: boolean = false;
  pickerSelection: number[] = [];

  buttons$ = new BehaviorSubject<ToolbarButton[]>([]);

  protected destroyRef = inject(DestroyRef);

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
    private postAlgorithmService: PostAlgorithmService,
  ) {
    this.attachedAlgorithms$ = this.store.pipe(
      select(selectAttachedFeedAlgorithms),
    );
    this.availableAlgorithms$ = this.store.pipe(
      select(selectAvailableFeedAlgorithms),
    );
    this.loading$ = this.store.pipe(select(selectFeedAlgorithmsLoading));
    this.pendingAlgorithmIds$ = this.store.pipe(
      select(selectPendingAlgorithmIds),
    );
  }

  ngOnInit(): void {
    this.store.dispatch(loadFeedAlgorithms({ feedId: this.objectId }));

    combineLatest([this.loading$, this.pendingAlgorithmIds$])
      .pipe(
        map(([loading, pending]) => ({
          loading,
          hasPending: pending.length > 0,
        })),
        distinctUntilChanged(
          (a, b) => a.loading === b.loading && a.hasPending === b.hasPending,
        ),
        tap(() => this.updateToolbarButtons()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.updateToolbarButtons();
  }

  isAlgorithmPending(id: number, pending: number[] | null): boolean {
    return !!pending?.includes(id);
  }

  detachAlgorithm(algorithm: FeedAlgorithm): void {
    this.store.dispatch(
      detachFeedAlgorithm({
        feedId: this.objectId,
        algorithmId: algorithm.id,
      }),
    );
  }

  editAlgorithm(algorithm: FeedAlgorithm): void {
    this.postAlgorithmService.editFeedAlgorithmParams(this.objectId, algorithm);
  }

  hasEditableParams(algorithm: FeedAlgorithm): boolean {
    return !!algorithm.params && !Array.isArray(algorithm.params);
  }

  openPicker(): void {
    this.pickerMode = true;
    this.pickerSelection = [];
    this.updateToolbarButtons();
  }

  closePicker(): void {
    this.pickerMode = false;
    this.pickerSelection = [];
    this.updateToolbarButtons();
  }

  onPickerSelectionChange(ids: number[]): void {
    this.pickerSelection = ids;
    this.updateToolbarButtons();
  }

  confirmAttach(): void {
    if (!this.pickerSelection.length) return;

    this.store.dispatch(
      attachFeedAlgorithms({
        feedId: this.objectId,
        algorithmIds: [...this.pickerSelection],
      }),
    );

    this.closePicker();
  }

  private updateToolbarButtons(): void {
    this.pendingAlgorithmIds$
      .pipe(
        take(1),
        tap((pending) => {
          const anyPending = pending.length > 0;
          const buttons: ToolbarButton[] = [
            // {
            //   type: 'button',
            //   variant: 'primary',
            //   label: this.translateService.instant(
            //     'feed-algorithms.button.attach',
            //   ),
            //   action: this.openPicker.bind(this),
            //   loading: false,
            //   disabled: anyPending,
            //   shouldDisplay: !this.pickerMode,
            // },
            // {
            //   type: 'button',
            //   variant: 'ghost',
            //   label: this.translateService.instant('btn.cancel'),
            //   action: this.closePicker.bind(this),
            //   shouldDisplay: this.pickerMode,
            // },
            // {
            //   type: 'button',
            //   variant: 'primary',
            //   label: this.translateService.instant(
            //     'feed-algorithms.button.confirm-attach',
            //   ),
            //   action: this.confirmAttach.bind(this),
            //   loading: anyPending,
            //   disabled: this.pickerSelection.length === 0,
            //   shouldDisplay: this.pickerMode,
            // },
          ];

          this.buttons$.next(buttons);
        }),
      )
      .subscribe();
  }
}
